// Hook for the official Google Drive Picker (GIS token client + Picker API).
// Loads the Google scripts on demand, requests an OAuth access_token (scope
// drive.file), then shows Google's native Drive picker.
import { useCallback, useEffect, useRef, useState } from "react";
import { lookup, interpolate } from "@/lib/i18n";
import { readStoredLang } from "./storedLang";

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
// drive.file is a NON-sensitive scope: the app only reaches the files explicitly picked
// in the Picker, so Google requires no verification for it.
const SCOPE = "https://www.googleapis.com/auth/drive.file";

// Reads the persisted language (cf. lib/ErrorBoundary.jsx): these callbacks run outside
// React rendering (script loading, Google SDK callbacks), so there is no useT() here —
// lookup() directly, as in ErrorBoundary.jsx.
const currentLang = readStoredLang;

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const el = document.createElement("script");
    el.src = src;
    el.async = true;
    el.onload = () => resolve();
    el.onerror = () =>
      reject(new Error(interpolate(lookup("common", "googleDrivePicker.scriptLoadFailed", currentLang()), { src })));
    document.body.appendChild(el);
  });
}

export default function useGoogleDrivePicker() {
  const configured = !!(CLIENT_ID && API_KEY);
  const [ready, setReady] = useState(false);
  const tokenClientRef = useRef(null);

  useEffect(() => {
    if (!configured) return;
    let cancelled = false;
    (async () => {
      try {
        await loadScript("https://apis.google.com/js/api.js");
        await loadScript("https://accounts.google.com/gsi/client");
        await new Promise((res) => window.gapi.load("picker", res));
        if (!cancelled) setReady(true);
      } catch {
        if (!cancelled) setReady(false);
      }
    })();
    return () => { cancelled = true; };
  }, [configured]);

  const openPicker = useCallback((onPicked, onError) => {
    if (!ready || !window.google?.accounts?.oauth2) {
      onError?.(new Error(lookup("common", "googleDrivePicker.notReady", currentLang())));
      return;
    }

    const buildPicker = (accessToken) => {
      const view = new window.google.picker.DocsView(window.google.picker.ViewId.DOCS)
        .setIncludeFolders(true)
        .setSelectFolderEnabled(false)
        .setMimeTypes("image/png,image/jpeg,image/gif,image/webp,video/mp4,video/quicktime,video/webm");
      const picker = new window.google.picker.PickerBuilder()
        .setDeveloperKey(API_KEY)
        .setOAuthToken(accessToken)
        .addView(view)
        .setCallback((data) => {
          if (data.action === window.google.picker.Action.PICKED) {
            const doc = data.docs?.[0];
            if (doc) {
              onPicked({ file_id: doc.id, filename: doc.name, access_token: accessToken });
            }
          }
        })
        .build();
      picker.setVisible(true);
    };

    if (!tokenClientRef.current) {
      tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
        client_id: CLIENT_ID,
        scope: SCOPE,
        // GIS defaults this to TRUE, which merges every scope the account ever granted this
        // OAuth client (the Calendar connection shares it) into the request — turning a
        // non-sensitive drive.file grant into a sensitive, unverified Calendar consent.
        include_granted_scopes: false,
        callback: (resp) => {
          if (resp.error) {
            onError?.(new Error(resp.error));
            return;
          }
          buildPicker(resp.access_token);
        },
      });
    }
    // Requests a fresh token (consent is silent when already granted).
    tokenClientRef.current.requestAccessToken({ prompt: "" });
  }, [ready]);

  return { configured, ready, openPicker };
}
